import React from 'react';
import { Flex } from 'antd';
import { CustomTitle } from '@/components';
import { SingleShipmentDataExtended } from '@/apis/types';
import { parseShipmentDayjs } from '@/common/utils/utils';

interface PizzaTrackerProps {
  shipment?: Partial<SingleShipmentDataExtended>;
  selectedTimezone?: string;
}

const PizzaTracker: React.FC<PizzaTrackerProps> = ({
  shipment,
  selectedTimezone,
}) => {
  if (!shipment) return null;
  const stages = shipment?.custom_status?.stages || [];
  if (!shipment?.custom_status || stages.length === 0) return null;

  // Example status config
  // const custom_status: StatusConfig = {
  //   current_stage: 'Order Received',
  //   stages: [
  //     { name: 'Order Received', is_completed: true },
  //     { name: 'Order in Progress', is_completed: false },
  //     { name: 'Ready for Pickup', is_completed: false },
  //     { name: 'In-Transit', is_completed: false },
  //     { name: 'Out for Delivery', is_completed: false },
  //     { name: 'Delivered', is_completed: false },
  //   ],
  // };

  const getStatusBucket = (status?: string): string => {
    const key = (status || '').toLowerCase().replace(/\s|-/g, '_');
    if (key === 'delivered') return 'delivered';
    if (key === 'out_for_delivery') return 'in_transit_or_ofd';
    if (key === 'in_transit') return 'in_transit_or_ofd';
    if (key === 'awaiting_pickup') return 'pre_pickup';
    if (key === 'pending') return 'pre_pickup';
    return 'unknown';
  };

  // ------------------------------
  // Format date to day + time
  // ------------------------------
  const getTZShortName = (timeZone: string) => {
    try {
      return new Date()
        .toLocaleTimeString('en-US', {
          timeZone,
          timeZoneName: 'short',
        })
        .split(' ')
        .pop(); // last token, e.g. "GMT", "PST", "EDT"
    } catch {
      return timeZone;
    }
  };
  const formatDateTime = (date?: string | null, timeZone?: string) => {
    if (!date) return null;
    const parsed = parseShipmentDayjs(date);
    if (!parsed) return null;
    const d = parsed.toDate();
    // Do NOT apply timezone here — just show user's machine interpretation
    const day = d.toLocaleDateString('en-US', {
      weekday: 'long',
    });
    const time = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    // Only **append the timezone label** (don't convert)
    const tz = timeZone ? ` ${getTZShortName(timeZone)}` : '';
    return { day, time: time + tz };
  };

  // ------------------------------
  // ETA fragments for time stamp
  // ------------------------------
  const getETAFragments = () => {
    const bucket = getStatusBucket(shipment?.status);

    const etaParts = formatDateTime(
      shipment?.estimated_delivery_date,
      selectedTimezone === 'local_scan_time'
        ? shipment?.origin_timezone
        : shipment?.timezone
    );
    const deliveredParts = formatDateTime(
      shipment?.actual_delivery_date,
      selectedTimezone === 'local_scan_time'
        ? shipment?.destination_timezone
        : shipment?.timezone
    );

    // Delivered
    if (bucket === 'delivered') {
      if (!deliveredParts) return null;
      return {
        prefix: `Delivered ${deliveredParts?.day}`,
        suffix: deliveredParts?.time,
      };
    }

    // ETA available
    if (etaParts) {
      return {
        prefix: `Estimated arrival ${etaParts?.day}`,
        suffix: etaParts?.time,
      };
    }

    // Pre-pickup + ETA unavailable
    if (bucket === 'pre_pickup') {
      return {
        prefix: 'Estimated arrival date available once shipped',
        suffix: '',
      };
    }

    // In-transit / OFD + ETA unavailable
    if (bucket === 'in_transit_or_ofd') {
      return {
        prefix: 'Estimated arrival date not currently available',
        suffix: '',
      };
    }

    return { prefix: 'ETA not available', suffix: '' };
  };

  const eta = getETAFragments();

  return (
    <div className='mt-4'>
      {/* Current Stage Title */}
      <CustomTitle
        text={shipment?.custom_status?.current_stage || shipment?.status}
        color='var(--text-primary)'
        fontSize='20px'
        lineHeight='28px'
        fontWeight='700'
        marginBottom='2px'
      />

      {/* ETA Display */}
      {eta && (
        <Flex gap={3} className='mb-2'>
          <CustomTitle
            text={eta?.prefix}
            fontSize='10px'
            fontWeight='400'
            lineHeight='16px'
            color='var(--text-light)'
            marginBottom='0px'
          />
          {eta?.suffix && (
            <CustomTitle
              text={eta?.suffix}
              fontSize='10px'
              fontWeight='400'
              lineHeight='16px'
              color='var(--text-primary)'
              marginBottom='0px'
              marginTop='0px'
            />
          )}
        </Flex>
      )}

      {/* Progress Bars */}
      <div className='mb-2'>
        <Flex gap={4} align='center'>
          {stages.map((stage, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                height: '4px',
                backgroundColor: stage?.is_completed ? '#212529' : '#D9D9D9',
                borderRadius: '5px',
              }}
            />
          ))}
        </Flex>
      </div>

      {/* Instruction Note */}
      {shipment?.instruction_note?.trim() && (
        <>
          <p className='zl-heading-12 mt-4 mb-6px'>Instruction</p>
          <div className='share-link-instructions-note'>
            <p>
              {shipment?.instruction_note?.length > 300
                ? shipment?.instruction_note.slice(0, 300) + '...'
                : shipment?.instruction_note}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PizzaTracker;
